import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  Stack,
  Link,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Avatar,
  Badge,
  IconButton
} from '@mui/material';
import {
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Roofing as RoofingIcon,
  Home as HomeIcon,
  Straighten as StraightenIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  RadioButtonUnchecked as UncheckedIcon
} from '@mui/icons-material';
import BeHead from '../../Components/Head/BeHead';

// Simule les projets avec étapes standardisées
const getProjectByName = (name) => {
  const dummyProjects = [
    {
      id: 1,
      name: 'TERRACE EXTERIEURE',
      client: 'Christian Ntopi',
      status: 'En cours',
      type: 'Aménagement extérieur',
      budget: 25000,
      submittedAt: '2025-06-01',
      responsible: 'Sophie Elenga',
      description: 'Création d\'une terrasse avec revêtement bois, éclairage intégré et mobilier outdoor.',
      internalNotes: 'Attente validation du devis par le client. Prévoir étude drainage.',
      attachments: [
        { label: 'Plan 3D', url: '/docs/terrasse-plan3d.pdf', type: 'pdf' },
        { label: 'Photos existant', url: '/images/terrasse-existant.jpg', type: 'image' },
        { label: 'Devis initial', url: '/docs/devis-terrasse.pdf', type: 'pdf' },
      ],
      steps: [
        {
          id: 1,
          title: 'Information préalable',
          status: 'completed',
          content: {
            clientInfo: {
              name: 'Christian Ntopi',
              email: 'christian.ntopi@email.com',
              phone: '+33 6 12 34 56 78',
              address: '123 Rue de la Paix, 75001 Paris'
            },
            projectInfo: {
              type: 'Terrasse extérieure',
              surface: '45 m²',
              budget: '25 000 €',
              deadline: '3 mois',
              description: 'Création d\'une terrasse avec revêtement bois, éclairage intégré et mobilier outdoor.'
            },
            requirements: [
              'Revêtement bois composite',
              'Éclairage LED intégré',
              'Mobilier outdoor',
              'Pergola bioclimatique',
              'Système d\'évacuation des eaux'
            ]
          }
        },
        {
          id: 2,
          title: 'CADRAGE DU PROJET',
          status: 'completed',
          content: {
            objectives: 'Créer un espace de vie extérieur fonctionnel et esthétique',
            constraints: 'Budget limité, délai serré, contraintes techniques',
            deliverables: 'Plans d\'exécution, devis détaillé, planning travaux'
          }
        },
        {
          id: 3,
          title: 'TOITURE',
          status: 'in_progress',
          content: {
            type: 'Pergola bioclimatique',
            materials: 'Aluminium et verre',
            dimensions: '6m x 4m',
            features: 'Lames orientables, éclairage intégré'
          }
        },
        {
          id: 4,
          title: 'MUR ET USAGE',
          status: 'pending',
          content: {
            wallType: 'Béton armé',
            usage: 'Espace de vie extérieur',
            thickness: '20 cm'
          }
        },
        {
          id: 5,
          title: 'CHARPENTE',
          status: 'pending',
          content: {
            type: 'Structure métallique',
            materials: 'Acier galvanisé',
            dimensions: '6m x 4m'
          }
        },
        {
          id: 6,
          title: 'DIMENSIONS ET PLANS',
          status: 'pending',
          content: {
            dimensions: {
              length: '6m',
              width: '4m',
              height: '2.5m',
              surface: '24m²'
            },
            plans: [
              { name: 'Plan de masse', url: '/plans/plan-masse.pdf' },
              { name: 'Plan d\'exécution', url: '/plans/plan-execution.pdf' },
              { name: 'Coupes', url: '/plans/coupes.pdf' },
              { name: 'Détails', url: '/plans/details.pdf' }
            ]
          }
        }
      ]
    }
  ];

  return dummyProjects.find((p) => p.name === decodeURIComponent(name));
};

// Couleurs des statuts
const getStatusColor = (status) => {
  switch (status) {
    case 'En cours':
      return 'info';
    case 'Etudié':
      return 'success';
    case 'Rejeté':
      return 'error';
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'pending':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="success" />;
    case 'in_progress':
      return <PendingIcon color="warning" />;
    case 'pending':
      return <UncheckedIcon color="disabled" />;
    default:
      return <UncheckedIcon color="disabled" />;
  }
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const BeProjectDetailsTabs = () => {
  const { nom } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const project = getProjectByName(nom);

  if (!project) {
    return (
      <Box p={4}>
        <Typography variant="h5">Projet non trouvé.</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }} variant="outlined">
          Retour
        </Button>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderStepContent = (step) => {
    switch (step.title) {
      case 'Information préalable':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    Informations client
                  </Typography>
                  <Stack spacing={2}>
                    <Typography><strong>Nom :</strong> {step.content.clientInfo.name}</Typography>
                    <Typography><strong>Email :</strong> {step.content.clientInfo.email}</Typography>
                    <Typography><strong>Téléphone :</strong> {step.content.clientInfo.phone}</Typography>
                    <Typography><strong>Adresse :</strong> {step.content.clientInfo.address}</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                    <AssignmentIcon sx={{ mr: 1 }} />
                    Informations projet
                  </Typography>
                  <Stack spacing={2}>
                    <Typography><strong>Type :</strong> {step.content.projectInfo.type}</Typography>
                    <Typography><strong>Surface :</strong> {step.content.projectInfo.surface}</Typography>
                    <Typography><strong>Budget :</strong> {step.content.projectInfo.budget}</Typography>
                    <Typography><strong>Délai :</strong> {step.content.projectInfo.deadline}</Typography>
                    <Typography><strong>Description :</strong> {step.content.projectInfo.description}</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              {/* <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Exigences du projet
                  </Typography>
                  <Grid container spacing={1}>
                    {step.content.requirements.map((req, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Chip label={req} variant="outlined" color="primary" />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card> */}
            </Grid>
          </Grid>
        );

      case 'DIMENSIONS ET PLANS':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                    <StraightenIcon sx={{ mr: 1 }} />
                    Dimensions
                  </Typography>
                  <Stack spacing={2}>
                    <Typography><strong>Longueur :</strong> {step.content.dimensions.length}</Typography>
                    <Typography><strong>Largeur :</strong> {step.content.dimensions.width}</Typography>
                    <Typography><strong>Hauteur :</strong> {step.content.dimensions.height}</Typography>
                    <Typography><strong>Surface :</strong> {step.content.dimensions.surface}</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" display="flex" alignItems="center">
                    <AttachFileIcon sx={{ mr: 1 }} />
                    Plans disponibles
                  </Typography>
                  <Grid container spacing={2}>
                    {step.content.plans.map((plan, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2" fontWeight="bold">
                                {plan.name}
                              </Typography>
                              <IconButton size="small" color="primary">
                                <DownloadIcon />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contenu de l'étape en cours de développement...
              </Typography>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <>
      <BeHead />
      <div className="content-body">
        <div className="container-fluid">
          {/* Header du projet */}
          <Box sx={{ bgcolor: 'background.paper', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: 'secondary.main2', color: 'white', p: 3 }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
                    <HomeIcon />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h4" fontWeight="bold">
                    {project.name}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {project.client} • {project.type}
                  </Typography>
                </Grid>
                <Grid item sx={{ml: 'auto'}}>
                  <Chip 
                    label={project.status} 
                    color={getStatusColor(project.status)}
                    sx={{ color: 'white', fontWeight: 'bold' }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Informations rapides */}
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3} justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Budget</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {project.budget.toLocaleString()} €
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Responsable</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {project.responsible}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Date soumission</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {new Date(project.submittedAt).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Pièces jointes</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {project.attachments.length} fichier(s)
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Onglets des étapes */}
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 64,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }
                }}
              >
                {project.steps.map((step, index) => (
                  <Tab
                    key={step.id}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(step.status)}
                        {step.title}
                      </Box>
                    }
                    icon={
                      index === 0 ? <InfoIcon /> :
                      index === project.steps.length - 1 ? <StraightenIcon /> :
                      <AssignmentIcon />
                    }
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>

            {/* Contenu des onglets */}
            {project.steps.map((step, index) => (
              <TabPanel key={step.id} value={activeTab} index={index}>
                {renderStepContent(step)}
              </TabPanel>
            ))}
          </Paper>

          {/* Actions */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate('/be-projects')}
            >
              Voir tous les projets
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/manage-be-step/${encodeURIComponent(project.name)}`)}
            >
              Modifier le projet
            </Button> */}
          </Box>
        </div>
      </div>
    </>
  );
};

export default BeProjectDetailsTabs; 